import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subscription, catchError, fromEvent, of, timer } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CrmAuthCookieService } from './crm-auth-cookie.service';
import { CrmTokenRefreshCoordinator } from './crm-token-refresh.coordinator';
// By file, not through the `libs/core` barrel — see the note in app.ts.
import { AuthService } from '../../../../../../../libs/core/auth/auth.service';

/**
 * Periodically checks the access-token expiry time.
 * When the token is within 60 seconds of expiring, calls the refresh API
 * and persists the new tokens.
 *
 * Also listens for the Page Visibility API 'visibilitychange' event.
 * When the tab becomes visible after being hidden (user returns to a
 * backgrounded tab), it proactively checks whether the token needs
 * refreshing. This prevents the "tab hangs on loading" issue where
 * browser timer throttling in background tabs prevents the periodic
 * check from running, allowing the token to expire undetected.
 *
 * The refresh itself is delegated to CrmTokenRefreshCoordinator rather than
 * called directly. Returning to a backgrounded tab fires this check at the same
 * moment the queued requests come back 401, and refresh tokens are single-use —
 * without the coordinator both paths spend the same token and the loser's 401
 * logs the user out of a healthy session.
 */
@Injectable({ providedIn: 'root' })
export class CrmTokenExpiryService implements OnDestroy {
  private readonly checkIntervalMs = 30_000; // check every 30s
  private readonly expiryBufferMs = 60_000; // refresh 60s before actual expiry
  private readonly authService = inject(AuthService);
  private readonly cookieService = inject(CrmAuthCookieService);
  private readonly refreshCoordinator = inject(CrmTokenRefreshCoordinator);

  private timerSub: Subscription | null = null;
  private visibilitySub: Subscription | null = null;

  start(): void {
    if (this.timerSub) return;

    this.timerSub = timer(0, this.checkIntervalMs).subscribe(() =>
      this.checkAndRefresh(),
    );

    // Listen for tab visibility changes.
    // When the user returns to a backgrounded tab, proactively check
    // and refresh the token. This mitigates browser timer throttling
    // in background tabs (Chrome throttles setInterval to ~1/min).
    if (typeof document !== 'undefined') {
      this.visibilitySub = fromEvent(document, 'visibilitychange')
        .pipe(throttleTime(5_000)) // prevent rapid-fire on tab switches
        .subscribe(() => {
          if (document.visibilityState === 'visible') {
            this.checkAndRefresh();
          }
        });
    }
  }

  stop(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = null;

    this.visibilitySub?.unsubscribe();
    this.visibilitySub = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private checkAndRefresh(): void {
    if (!this.authService.isAuthenticated()) return;

    const accessExpiresAt = this.cookieService.getAccessTokenExpiresAt();

    if (accessExpiresAt === null) {
      // Expiry unknown (no access token, or unreadable expiry data). Silently
      // skipping here is what left a returning user on a dead token forever, so
      // treat it as "assume stale" and let the coordinator decide — it already
      // bails when there is no usable refresh token.
      this.doRefresh();
      return;
    }

    const remainingMs = accessExpiresAt - Date.now();

    if (remainingMs > this.expiryBufferMs) {
      return;
    }

    // Expiry within buffer — call refresh
    this.doRefresh();
  }

  /**
   * A proactive refresh is best-effort: nothing is waiting on it, and the 401
   * interceptor is still there to recover if it fails. Errors are swallowed so a
   * flaky network never ends a session that is still perfectly valid.
   *
   * De-duplication (in-tab and cross-tab) is the coordinator's job, so there is
   * no in-flight flag here — a second call while one is running simply joins it.
   */
  private doRefresh(): void {
    this.refreshCoordinator
      .refresh()
      .pipe(catchError(() => of(null)))
      .subscribe();
  }
}
